import { useRef, useState } from 'react'
import { SOURCE_LABEL, sourceOf } from './evidence'
import { stepTitle } from './EvidenceMap'
import { stepSummary } from './stepSummary'
import type { InvestigationResult, InvestigationStep, LearningMatch } from '@/types/investigation'

function MatchAttempt({ match }: { match: LearningMatch }) {
  return (
    <section className="mb-4 rounded-etn-card border border-etn-border-hairline p-4" data-testid="learning-match">
      <h3 className="mb-1 text-sm font-semibold">Known-fix check</h3>
      <p className="mb-3 text-xs text-etn-text-soft">
        {match.shortCircuited
          ? `Answered by "${match.bestTitle}": every confirmation check passed.`
          : match.checks.some(c => !c.passed)
            ? 'A confirmation check failed, so the Support Agent investigated in full with the best candidate as a hint.'
            : match.checks.length
              ? 'Every check passed, but the fix could not be completed from what they read, so the Support Agent investigated in full with it as a hint.'
              : 'No known fix was a confident enough match, so the Support Agent investigated in full.'}
      </p>
      <ul className="mb-3 space-y-1 text-xs">
        {match.candidates.map(c => (
          <li key={c.learningId}>
            <span className="font-semibold tabular-nums">{c.confidence}%</span> {c.title}
            {c.reasoning ? <span className="text-etn-text-soft">. {c.reasoning}</span> : null}
          </li>
        ))}
      </ul>
      {match.checks.length > 0 && (
        <ul className="space-y-1 text-xs">
          {match.checks.map((c, i) => (
            <li key={i}>
              <span
                className={`mr-2 rounded-etn-pill px-2 py-0.5 text-etn-micro font-semibold ${c.passed ? 'bg-etn-success-subtle text-etn-success-text' : 'bg-etn-danger-subtle text-etn-danger-text'}`}
              >
                {c.passed ? 'Passed' : 'Failed'}
              </span>
              {c.tool}: {c.expectation}
              {c.reasoning ? <span className="text-etn-text-soft">. {c.reasoning}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function took(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}

/**
 * Layout C from the approved mockup: every step on the left, the selected one's full arguments and
 * raw output on the right, with the findings that cite it.
 */
export function StepsInspector({
  steps,
  result,
  learningMatch
}: {
  steps: InvestigationStep[]
  result?: InvestigationResult | null
  learningMatch?: LearningMatch | null
}) {
  const [selected, setSelected] = useState(0)
  const tabsRef = useRef<(HTMLButtonElement | null)[]>([])
  const match = learningMatch?.candidates.length ? <MatchAttempt match={learningMatch} /> : null
  if (!steps.length)
    return (
      <>
        {match}
        <p className="py-6 text-sm text-etn-text-soft">This run made no tool calls.</p>
      </>
    )

  const step = steps[Math.min(selected, steps.length - 1)]
  const usedIn = (result?.findings ?? []).filter(f => f.evidence.includes(selected)).map(f => f.title)

  return (
    <>
      {match}
      <div className="grid min-h-[460px] grid-cols-1 overflow-hidden rounded-etn-card border border-etn-border-hairline md:grid-cols-[300px_minmax(0,1fr)]">
        <div
          role="tablist"
          aria-label="Investigation steps"
          aria-orientation="vertical"
          className="max-h-[260px] overflow-y-auto border-b border-etn-border-hairline bg-etn-surface-sunken p-2 md:max-h-none md:border-b-0 md:border-r"
        >
          {steps.map((s, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              id={`step-tab-${i}`}
              ref={el => {
                tabsRef.current[i] = el
              }}
              aria-selected={i === selected}
              aria-controls="step-detail"
              tabIndex={i === selected ? 0 : -1}
              onClick={() => setSelected(i)}
              onKeyDown={e => {
                const next = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: steps.length - 1 }[e.key]
                if (next === undefined) return
                e.preventDefault()
                const target = (next + steps.length) % steps.length
                setSelected(target)
                tabsRef.current[target]?.focus()
              }}
              className={`grid w-full grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-2 py-1.5 text-left text-etn-control ${
                i === selected ? 'bg-etn-surface font-semibold shadow-etn-whisper' : 'text-etn-text-cell hover:bg-etn-surface'
              }`}
            >
              <span className="font-mono text-etn-micro font-bold text-etn-text-soft">{SOURCE_LABEL[sourceOf(s.tool)]}</span>
              <span className="min-w-0">
                <span className="block truncate">{stepTitle(s)}</span>
                <span className="block truncate text-etn-micro font-normal text-etn-text-soft">{stepSummary(s)}</span>
              </span>
              <span className="text-etn-micro tabular-nums text-etn-text-soft">
                {s.isError ? 'Skipped ' : ''}
                {took(s.durationMs)}
              </span>
            </button>
          ))}
        </div>
        <div id="step-detail" role="tabpanel" aria-labelledby={`step-tab-${selected}`} className="min-w-0 p-5" data-testid="step-detail">
          <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
            {stepTitle(step)}
            {step.isError && (
              <span className="rounded-etn-pill bg-etn-danger-subtle px-2 py-0.5 text-etn-micro font-semibold text-etn-danger-text">Skipped</span>
            )}
          </h3>
          <p className="mb-1 max-w-[70ch] text-etn-control text-etn-text-cell" data-testid="step-summary">
            {stepSummary(step)}
          </p>
          {step.why && <p className="mb-3.5 max-w-[70ch] text-etn-control text-etn-text-label">{step.why}</p>}
          <dl className="mb-3.5 grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-etn-control">
            <dt className="text-etn-text-soft">Tool</dt>
            <dd className="font-mono text-xs">{step.tool}</dd>
            <dt className="text-etn-text-soft">Step</dt>
            <dd>{selected}</dd>
            <dt className="text-etn-text-soft">Took</dt>
            <dd>{took(step.durationMs)}</dd>
          </dl>
          <p className="mb-1.5 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Arguments</p>
          <pre className="mb-3.5 overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-etn-surface-sunken p-3 font-mono text-xs">
            {JSON.stringify(step.args ?? {}, null, 2)}
          </pre>
          <p className="mb-1.5 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">Result</p>
          <pre
            className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-etn-surface-sunken p-3 font-mono text-xs"
            data-testid="step-output"
          >
            {step.resultPreview}
          </pre>
          {usedIn.length > 0 && (
            <div className="mt-3.5 flex flex-wrap items-center gap-2 text-xs text-etn-text-label" data-testid="used-in">
              Used in:
              {usedIn.map(title => (
                <span key={title} className="rounded-etn-pill bg-etn-success-subtle px-2.5 py-0.5 font-semibold text-etn-success-text">
                  {title}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
